import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTeams, createTeam, updateTeam, deleteTeam, getManagers } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Plus, Edit, Trash2, Users, Percent, Loader2, UserCircle, Crown } from 'lucide-react';
import { toast } from 'sonner';

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [allManagers, setAllManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // Team dialog state
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [leaderPercent, setLeaderPercent] = useState('5');
  const [leaderManagerId, setLeaderManagerId] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [teamsData, managersData] = await Promise.all([
        getTeams(),
        getManagers().catch(() => [])
      ]);
      setTeams(teamsData);
      setAllManagers(managersData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/weeks');
      return;
    }
    fetchData();
  }, [fetchData, isAdmin, navigate]);

  const openCreateTeamDialog = () => {
    setEditingTeam(null);
    setTeamName('');
    setLeaderPercent('5');
    setLeaderManagerId('');
    setTeamDialogOpen(true);
  };

  const openEditTeamDialog = (team) => {
    setEditingTeam(team);
    setTeamName(team.name);
    setLeaderPercent(team.leader_percent?.toString() || '5');
    setLeaderManagerId(team.leader_manager_id || '');
    setTeamDialogOpen(true);
  };

  const handleSaveTeam = async () => {
    if (!teamName) return;

    try {
      setSaving(true);
      const teamData = {
        name: teamName,
        leader_percent: parseFloat(leaderPercent) || 5,
        leader_manager_id: leaderManagerId || null,
      };

      if (editingTeam) {
        await updateTeam(editingTeam.id, teamData);
        toast.success('Команда обновлена');
      } else {
        await createTeam(teamData);
        toast.success('Команда создана');
      }

      setTeamDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Failed to save team:', err);
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteTeam = (team) => {
    setDeletingTeam(team);
    setDeleteDialogOpen(true);
  };

  const handleDeleteTeam = async () => {
    if (!deletingTeam) return;

    try {
      await deleteTeam(deletingTeam.id);
      toast.success('Команда удалена');
      setDeleteDialogOpen(false);
      setDeletingTeam(null);
      fetchData();
    } catch (err) {
      console.error('Failed to delete team:', err);
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  // Group managers by team
  const getTeamManagers = (teamId) => {
    return allManagers.filter(m => m.team_id === teamId);
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Команды</h1>
          <p className="text-muted-foreground">Управление командами и тимлидами</p>
        </div>
        <Button onClick={openCreateTeamDialog} data-testid="create-team-btn">
          <Plus className="h-4 w-4 mr-2" />
          Создать команду
        </Button>
      </div>

      {teams.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Нет команд</h3>
            <p className="text-muted-foreground mb-4">Создайте первую команду</p>
            <Button onClick={openCreateTeamDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Создать команду
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="multiple" defaultValue={teams.map(t => t.id)} className="space-y-4">
          {teams.map((team) => {
            const teamManagers = getTeamManagers(team.id);
            return (
              <AccordionItem key={team.id} value={team.id} className="border rounded-lg" data-testid={`team-${team.id}`}>
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <span className="font-medium">{team.name}</span>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{teamManagers.length} менеджеров</span>
                          <span>•</span>
                          <span>{team.leader_percent}% тимлиду</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditTeamDialog(team)}
                        data-testid={`edit-team-${team.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => confirmDeleteTeam(team)}
                        data-testid={`delete-team-${team.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  {/* Leader info */}
                  {team.leader_name && (
                    <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-amber-600" />
                        <span className="text-sm font-medium">Тимлид:</span>
                        <span className="text-sm">{team.leader_name}</span>
                        <span className="text-xs text-muted-foreground">(может работать в другой команде)</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Managers list */}
                  {teamManagers.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">
                      Нет менеджеров в команде
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {teamManagers.map((manager) => (
                        <div
                          key={manager.id}
                          className="flex items-center justify-between p-3 rounded-lg border"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <UserCircle className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <span className="font-medium">{manager.name}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{manager.percent_standard}% / {manager.percent_above_1000}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* Create/Edit Team Dialog */}
      <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTeam ? 'Редактировать команду' : 'Создать команду'}
            </DialogTitle>
            <DialogDescription>
              {editingTeam ? 'Измените данные команды' : 'Создайте новую команду'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Название команды</Label>
              <Input
                placeholder="Введите название"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                data-testid="team-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Процент тимлида</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="5"
                  value={leaderPercent}
                  onChange={(e) => setLeaderPercent(e.target.value)}
                  min="0"
                  max="100"
                  className="w-24"
                  data-testid="team-leader-percent-input"
                />
                <span className="text-muted-foreground">% от общего залива команды</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-amber-600" />
                Тимлид (менеджер)
              </Label>
              <Select value={leaderManagerId || "none"} onValueChange={(val) => setLeaderManagerId(val === "none" ? "" : val)}>
                <SelectTrigger data-testid="team-leader-select">
                  <SelectValue placeholder="Выберите тимлида" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Не назначен</SelectItem>
                  {allManagers.map((manager) => (
                    <SelectItem key={manager.id} value={manager.id}>
                      {manager.name} {manager.team_name ? `(${manager.team_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Тимлид получает % от общего залива этой команды. Он может работать в любой команде как менеджер.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSaveTeam}
              disabled={!teamName || saving}
              data-testid="confirm-save-team"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить команду?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить команду "{deletingTeam?.name}"?
              Нельзя удалить команду, в которой есть менеджеры.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTeam}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-team"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
